import { useCallback, useEffect, useState } from 'react'
import { tenantUserListSpec, tenantUserSaveSpec, tenantUserDeleteSpec, type TenantUserRow, UserRole, TenantUserRole } from 'aevocam-rest-api-spec'
import { useApi } from '@/hooks/useApi'
import { FilterTextBox } from '@/components/scalar/FilterTextBox'
import { toastError, toastSuccess, toastWarning } from '@/components/action/Toaster'
import {
  startLoading,
  doneLoading,
  incrementRefresh,
  type DataGridState,
} from '@/components/dataGrid/createDataGridState'
import { DataGrid, type DataGridColumn, type DataGridRow } from '@/components/dataGrid/DataGrid'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'
import { DataPager } from '@/components/dataGrid/DataPager'
import { SelectBox } from '@/components/scalar/SelectBox'
import { IconButton } from '@/components/action/IconButton'

//TODO: Can't remove all owners

type Props = {
  tenantId: string
}

export function TenantMembers({ tenantId }: Props) {
  const api = useApi({ requireLogin: true })
  const userRole = api.status.session?.userRole
  const tenantUserRole = api.status.session?.tenantUserRole
  const canEdit =
    userRole === UserRole.Admin ||
    tenantUserRole === TenantUserRole.Owner ||
    tenantUserRole === TenantUserRole.Admin

  //--------------------------------------------------------------------------------
  const [userListState, setUserListState] = useState<TenantUserListState>({
    filterText: '',
    rows: [],
    totalRows: 0,
    skip: 0,
    take: 10,
  })

  //--------------------------------------------------------------------------------
  const setMembersFilterText = useCallback((filterText: string) => {
    setUserListState(prev => ({ ...prev, filterText, skip: 0 }))
  }, [])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    async function fn() {
      startLoading(setUserListState as GlobalStateSetter<DataGridState<TenantUserRow>>, userListState.refreshQuietly)
      try {
        const refreshIncrement = userListState.refresh ?? 0
        const res = await api.request(tenantUserListSpec, {
          path: { tenant: tenantId },
          body: {
            filterText: userListState.filterText,
            skip: userListState.skip,
            take: userListState.take,
          },
        })
        if (res.status === 200 && res.data?.users) {
          setUserListState(prev => {
            if (prev.refresh !== refreshIncrement) return prev
            return {
              ...prev,
              rows: res.data?.users?.rows ?? [],
              totalRows: res.data?.users?.totalRows ?? 0,
            }
          })
        }
      } catch {
        toastError('Failed to load members')
      } finally {
        doneLoading(setUserListState as GlobalStateSetter<DataGridState<TenantUserRow>>)
      }
    }
    void fn()
  }, [tenantId, api, userListState.skip, userListState.take, userListState.refresh, userListState.filterText])

  //--------------------------------------------------------------------------------
  const handleRemoveMember = useCallback(async (row: TenantUserRow) => {
    if (row.userId == null) return
    if (!confirm('Remove this user from the tenant? They will lose access.')) return
    const res = await api.request(tenantUserDeleteSpec, {
      path: { tenant: tenantId, user: String(row.userId) },
    })
    if (res.status === 200) {
      toastWarning('Member removed')
      incrementRefresh(setUserListState)
    } else {
      toastError(res.error ?? 'Failed to remove member')
    }
  }, [tenantId, api, setUserListState, toastError, toastWarning])

  //--------------------------------------------------------------------------------
  const handleMemberRoleChange = useCallback(async (row: TenantUserRow, newRole: TenantUserRole) => {
    if (row.userId == null) return
    const res = await api.request(tenantUserSaveSpec, {
      path: { tenant: tenantId, user: String(row.userId) },
      body: { role: newRole },
    })
    if (res.status === 200) {
      toastSuccess('Role updated')
      incrementRefresh(setUserListState)
    } else {
      toastError(res.error ?? 'Failed to update role')
    }
  }, [tenantId, api, setUserListState, toastError, toastSuccess])

  //--------------------------------------------------------------------------------
  return (
    <div className='Cutout Wrapped'>
      <h2>Members</h2>

      <div className='FlexRow AlignCenter'>
        <div className='FlexGrow'>
          <FilterTextBox
            value={userListState.filterText ?? ''}
            setValue={setMembersFilterText}
          />
        </div>
        <DataPager<TenantUserListState>
          state={userListState}
          setState={setUserListState}
          alignRight
        />
      </div>

      <DataGrid
        state={userListState}
        setState={setUserListState}
        idColumn='userId'
        columns={tenantUserColumns(canEdit, handleRemoveMember, handleMemberRoleChange)}
      />
    </div>
  )
}


//--------------------------------------------------------------------------------
type TenantUserListState = DataGridState<TenantUserRow> & {
  filterText?: string
}

//--------------------------------------------------------------------------------
const TENANT_USER_ROLE_OPTIONS: { label: string; value: TenantUserRole }[] = [
  { label: TenantUserRole[TenantUserRole.Owner], value: TenantUserRole.Owner },
  { label: TenantUserRole[TenantUserRole.Admin], value: TenantUserRole.Admin },
  { label: TenantUserRole[TenantUserRole.Viewer], value: TenantUserRole.Viewer },
  //{ label: TenantUserRole[TenantUserRole.Other], value: TenantUserRole.Other },
]

//--------------------------------------------------------------------------------
function renderMemberActions(
  onRemoveMember: (row: TenantUserRow) => void | Promise<void>,
  canEdit: boolean,
) {
  return (_value: unknown, { row }: DataGridRow<TenantUserRow>) => (
    <IconButton
      icon='times'
      tooltip='Remove member'
      disabled={!canEdit}
      onClick={() => onRemoveMember(row)}
    />
  )
}

//--------------------------------------------------------------------------------
function renderMemberRole(
  onRoleChange: (row: TenantUserRow, newRole: TenantUserRole) => void | Promise<void>,
  canEdit: boolean,
) {
  return (value: TenantUserRole | undefined, { row }: DataGridRow<TenantUserRow>) => {
    const currentRole = value ?? TenantUserRole.Other
    return (
      <SelectBox<TenantUserRole>
        options={TENANT_USER_ROLE_OPTIONS}
        valueToString={v => (v != null ? String(v) : '')}
        valueFromString={s => Number(s) as TenantUserRole}
        value={currentRole}
        setValue={next => {
          const newRole = typeof next === 'function' ? next(currentRole) : next
          void onRoleChange(row, newRole)
        }}
        disabled={!canEdit}
      />
    )
  }
}

//--------------------------------------------------------------------------------
function tenantUserColumns(
  canEdit: boolean,
  onRemoveMember: (row: TenantUserRow) => void | Promise<void>,
  onRoleChange: (row: TenantUserRow, newRole: TenantUserRole) => void | Promise<void>,
): DataGridColumn<TenantUserRow>[] {
  return [
    {
      id: 'userId',
      label: '',
      className: () => 'NoWrap',
      render: renderMemberActions(onRemoveMember, canEdit),
    },
    { id: 'name', label: 'Name' },
    { id: 'email', label: 'Email' },
    {
      id: 'role',
      label: 'Role',
      render: renderMemberRole(onRoleChange, canEdit),
    },
  ]
}
